import React from "react";
import Footer from "../../layout/Footer";
import Header from "../../layout/Header";
import Sidebar from "../../layout/Sidebar";

/**
 * Coquille commune à toutes les pages applicatives.
 * `scroll` : le contenu défile dans la page (tableaux de bord, listes de cartes).
 * Par défaut le contenu occupe exactement la hauteur utile et ce sont les
 * panneaux internes qui gèrent leur propre défilement.
 */
export const PageShell = ({
  children,
  scroll = false,
  chrome = true,
}: {
  children: React.ReactNode;
  scroll?: boolean;
  chrome?: boolean;
}) => (
  <div className="flex h-screen w-full flex-col overflow-hidden bg-slate-100">
    {chrome && <Header />}
    {chrome && <Sidebar />}

    <main
      className={`min-h-0 flex-1 pb-9 md:pl-24 ${
        scroll ? "overflow-y-auto" : "overflow-hidden"
      }`}
    >
      <div
        className={`mx-auto flex w-full max-w-[1800px] flex-col gap-3 px-3 py-3 sm:px-5 sm:py-4 3xl:max-w-[2400px] ${
          scroll ? "min-h-full" : "h-full"
        }`}
      >
        {children}
      </div>
    </main>

    {chrome && <Footer />}
  </div>
);

/**
 * Bandeau de contexte en haut de page : sur-titre, titre, pastille et actions.
 */
export const PageHeader = ({
  eyebrow,
  title,
  badge,
  description,
  actions,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  badge?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
}) => (
  <section className="shrink-0 rounded-2xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm sm:px-5">
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-6">
      <div className="min-w-0">
        {eyebrow ? (
          <div className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
            {eyebrow}
          </div>
        ) : null}

        <div
          className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 ${
            eyebrow ? "mt-1.5" : ""
          }`}
        >
          <h1 className="truncate text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
            {title}
          </h1>
          {badge}
        </div>

        {description ? (
          <p className="mt-1 font-Nunito text-sm text-slate-500">
            {description}
          </p>
        ) : null}
      </div>

      {actions ? (
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {actions}
        </div>
      ) : null}
    </div>
  </section>
);

/**
 * Pastille de contexte (nom du dossier client, période…).
 */
export const ContextBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100 ring-inset sm:text-sm">
    <span
      aria-hidden="true"
      className="size-1.5 shrink-0 rounded-full bg-indigo-500"
    />
    <span className="truncate">{children}</span>
  </span>
);

/**
 * Panneau blanc principal : occupe la hauteur restante et confine le défilement.
 */
export const PagePanel = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <section
    className={`flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`}
  >
    {children}
  </section>
);

/**
 * Barre d'outils d'un panneau : recherche à gauche, compteur / filtres à droite.
 */
export const PanelToolbar = ({ children }: { children: React.ReactNode }) => (
  <div className="flex shrink-0 flex-col gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
    {children}
  </div>
);

export const ToolbarRow = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
  >
    {children}
  </div>
);

/**
 * Champ de recherche contrôlé : la valeur reste gérée par la page appelante.
 */
export const SearchField = ({
  value,
  onChange,
  placeholder = "Rechercher…",
  className = "",
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) => (
  <div className={`relative w-full sm:max-w-sm ${className}`}>
    <svg
      aria-hidden="true"
      className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z"
      />
    </svg>
    <input
      type="search"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label="Rechercher"
      className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pr-9 pl-9 font-Nunito text-sm text-slate-800 transition-colors placeholder:text-slate-400 focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100 focus:outline-none [&::-webkit-search-cancel-button]:hidden"
    />
    {value !== "" && (
      <button
        type="button"
        onClick={() => onChange("")}
        aria-label="Effacer la recherche"
        className="absolute top-1/2 right-2.5 inline-flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
      >
        <svg
          aria-hidden="true"
          className="size-3.5"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.4}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    )}
  </div>
);

type ButtonVariant = "primary" | "secondary" | "success" | "soft" | "danger";

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:
    "bg-dca-blue text-white shadow-sm hover:bg-indigo-700 focus-visible:ring-indigo-300",
  secondary:
    "border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus-visible:ring-slate-300",
  success:
    "bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-emerald-300",
  soft: "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 ring-inset hover:bg-indigo-100 focus-visible:ring-indigo-300",
  danger:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 focus-visible:ring-red-300",
};

/**
 * Bouton d'action de page. `icon` est rendu avant le libellé.
 */
export const ActionButton = ({
  children,
  onClick,
  variant = "secondary",
  icon,
  type = "button",
  form,
  disabled = false,
  title,
  className = "",
}: {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  variant?: ButtonVariant;
  icon?: React.ReactNode;
  type?: "button" | "submit";
  form?: string;
  disabled?: boolean;
  title?: string;
  className?: string;
}) => (
  <button
    type={type}
    form={form}
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`inline-flex cursor-pointer items-center justify-center gap-2 rounded-xl px-3.5 py-2.5 font-Nunito text-sm font-semibold whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${VARIANT_CLASSES[variant]} ${className}`}
  >
    {icon}
    {children}
  </button>
);

export const PlusIcon = () => (
  <svg
    aria-hidden="true"
    className="size-4 shrink-0"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14m-7-7h14" />
  </svg>
);

/**
 * Bouton de fermeture / retour, aligné avec les autres actions de page.
 */
export const CloseButton = ({
  onClick,
  label = "Fermer",
}: {
  onClick: () => void;
  label?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    className="inline-flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600 focus-visible:ring-2 focus-visible:ring-red-200 focus-visible:outline-none"
  >
    <svg
      aria-hidden="true"
      className="size-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  </button>
);

/**
 * Sélecteur d'onglets segmenté, défilable horizontalement sur mobile.
 */
export const TabBar = ({
  tabs,
  value,
  onChange,
  className = "",
}: {
  tabs: { id: string; label: string }[];
  value: string;
  onChange: (id: string) => void;
  className?: string;
}) => (
  <div
    role="tablist"
    className={`-mx-1 flex gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 [scrollbar-width:none] sm:mx-0 sm:overflow-visible [&::-webkit-scrollbar]:hidden ${className}`}
  >
    {tabs.map((tab) => {
      const isActive = tab.id === value;
      return (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(tab.id)}
          className={`flex-1 cursor-pointer rounded-lg px-3 py-2 font-Nunito text-sm font-semibold whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:outline-none ${
            isActive
              ? "bg-white text-indigo-700 shadow-sm"
              : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {tab.label}
        </button>
      );
    })}
  </div>
);

/**
 * Rangée de boutons de tri.
 */
export const SortBar = ({
  children,
  label = "Trier par",
}: {
  children: React.ReactNode;
  label?: string;
}) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
      {label}
    </span>
    {children}
  </div>
);

/**
 * Compteur de résultats affiché à droite de la barre d'outils.
 */
export const ResultCount = ({
  count,
  singular,
  plural,
  filtered = false,
}: {
  count: number;
  singular: string;
  plural: string;
  filtered?: boolean;
}) => (
  <p aria-live="polite" className="font-Nunito text-xs text-slate-500 sm:text-sm">
    <span className="font-bold text-slate-800 tabular-nums">{count}</span>{" "}
    {count > 1 ? plural : singular}
    {filtered ? ` trouvé${count > 1 ? "s" : ""}` : ""}
  </p>
);

/**
 * Petite statistique mise en avant (temps saisi, totaux…).
 */
export const StatChip = ({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: React.ReactNode;
  tone?: "neutral" | "indigo" | "emerald" | "amber";
}) => {
  const tones = {
    neutral: "bg-slate-50 text-slate-700 ring-slate-200",
    indigo: "bg-indigo-50 text-indigo-700 ring-indigo-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    amber: "bg-amber-50 text-amber-800 ring-amber-100",
  };

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-xl px-3 py-2 ring-1 ring-inset ${tones[tone]}`}
    >
      <span className="text-[11px] font-bold tracking-wider uppercase opacity-70">
        {label}
      </span>
      <span className="font-Nunito text-sm font-bold tabular-nums">
        {value}
      </span>
    </div>
  );
};

/**
 * Zone de contenu centrée d'un panneau (chargement, message).
 */
export const PanelCenter = ({ children }: { children: React.ReactNode }) => (
  <div className="flex min-h-0 flex-1 items-center justify-center p-6">
    {children}
  </div>
);

/**
 * En-tête des panneaux de détail affichés à l'intérieur d'un `PagePanel`
 * (création / consultation d'un dossier, d'une mission, d'une facture…).
 * `hint` remplace les anciens bandeaux de consigne.
 */
export const DetailHeader = ({
  title,
  hint,
  actions,
  onClose,
}: {
  title: React.ReactNode;
  hint?: React.ReactNode;
  actions?: React.ReactNode;
  onClose?: () => void;
}) => (
  <div className="shrink-0 border-b border-slate-100 px-4 py-3 sm:px-5">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <h3 className="truncate text-lg font-bold tracking-tight text-slate-900">
          {title}
        </h3>
        {hint ? (
          <p className="mt-0.5 font-Nunito text-sm text-slate-500">{hint}</p>
        ) : null}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {actions}
        {onClose ? <CloseButton onClick={onClose} /> : null}
      </div>
    </div>
  </div>
);

/**
 * Carte de regroupement de champs à l'intérieur d'un panneau de détail.
 */
export const DetailCard = ({
  title,
  actions,
  children,
  className = "",
  bodyClassName = "p-4",
}: {
  title?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Rembourrage du corps : à réduire pour les colonnes étroites, à vider
   * pour coller une liste ou un tableau bord à bord. */
  bodyClassName?: string;
}) => (
  <section
    className={`rounded-xl border border-slate-200 bg-white ${className}`}
  >
    {title || actions ? (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-2.5">
        {title ? (
          <h4 className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
            {title}
          </h4>
        ) : (
          <span />
        )}
        {actions}
      </div>
    ) : null}
    <div className={bodyClassName}>{children}</div>
  </section>
);
